import { bonuses } from '../data/bonuses'
import { faqs } from '../data/faq'
import { checkoutUrl, heroImage, price } from '../data/copy'

const TITLE =
  'The Art Flow Archive™ — Complete Art Teaching System | 6 Volumes + 6 Bonuses'

const DESCRIPTION =
  'Plan your entire year of art classes today. 6 master volumes, 150+ ready-to-teach lessons, a 365-day visual system and 6 strategic PDF bonuses. Instant digital access.'

const SITE_NAME = 'The Art Flow Archive'

function upsertMeta(
  key: 'name' | 'property',
  value: string,
  content: string,
): void {
  let tag = document.head.querySelector<HTMLMetaElement>(
    `meta[${key}="${value}"]`,
  )
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(key, value)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

function upsertLink(
  rel: string,
  href: string,
  attrs: Record<string, string> = {},
): HTMLLinkElement {
  const selector = `link[rel="${rel}"][href="${href}"]`
  let link = document.head.querySelector<HTMLLinkElement>(selector)
  if (!link) {
    link = document.createElement('link')
    link.rel = rel
    link.href = href
    document.head.appendChild(link)
  }
  Object.entries(attrs).forEach(([name, val]) => link?.setAttribute(name, val))
  return link
}

/**
 * Pulls the LCP image and the web fonts forward without blocking the first
 * paint: the hero mockup is preloaded at high priority, while the stylesheet
 * is fetched with `media="print"` and promoted to `all` once it lands.
 */
function loadCriticalAssets(): void {
  upsertLink('preload', heroImage, {
    as: 'image',
    fetchpriority: 'high',
  })

  upsertLink('preconnect', 'https://fonts.googleapis.com')
  upsertLink('preconnect', 'https://fonts.gstatic.com', { crossorigin: '' })

  const fontsHref =
    'https://fonts.googleapis.com/css2?family=Marcellus&family=Inter:wght@400;500;600;700&display=swap'

  if (!document.head.querySelector(`link[href="${fontsHref}"]`)) {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = fontsHref
    link.media = 'print'
    link.addEventListener('load', () => {
      link.media = 'all'
    })
    document.head.appendChild(link)
  }
}

function injectJsonLd(id: string, data: unknown): void {
  let script = document.getElementById(id) as HTMLScriptElement | null
  if (!script) {
    script = document.createElement('script')
    script.id = id
    script.type = 'application/ld+json'
    document.head.appendChild(script)
  }
  script.textContent = JSON.stringify(data)
}

/**
 * Sets every document-level SEO signal at runtime: title, description, robots,
 * canonical, Open Graph / Twitter cards, and Product + FAQ structured data.
 */
export function initHead(): void {
  if (typeof document === 'undefined') return

  const url =
    typeof window !== 'undefined' ? window.location.origin + '/' : undefined

  document.documentElement.lang = 'en'
  document.title = TITLE

  upsertMeta('name', 'description', DESCRIPTION)
  upsertMeta('name', 'robots', 'index, follow, max-image-preview:large')
  upsertMeta('name', 'theme-color', '#FBF7F0')
  upsertMeta(
    'name',
    'viewport',
    'width=device-width, initial-scale=1, viewport-fit=cover',
  )

  upsertMeta('property', 'og:type', 'product')
  upsertMeta('property', 'og:site_name', SITE_NAME)
  upsertMeta('property', 'og:title', TITLE)
  upsertMeta('property', 'og:description', DESCRIPTION)
  upsertMeta('property', 'og:image', heroImage)
  upsertMeta('property', 'og:image:alt', `${SITE_NAME} complete collection`)
  upsertMeta('property', 'og:locale', 'en_US')
  if (url) upsertMeta('property', 'og:url', url)

  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', TITLE)
  upsertMeta('name', 'twitter:description', DESCRIPTION)
  upsertMeta('name', 'twitter:image', heroImage)

  if (url) upsertLink('canonical', url)

  loadCriticalAssets()

  injectJsonLd('ld-product', {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'The Art Flow Archive™',
    description: DESCRIPTION,
    image: [heroImage, ...bonuses.map((bonus) => bonus.image)],
    brand: { '@type': 'Brand', name: SITE_NAME },
    category: 'Art teaching curriculum',
    offers: {
      '@type': 'Offer',
      url: checkoutUrl,
      price: price.replace(/[^0-9.]/g, ''),
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
    },
  })

  injectJsonLd('ld-faq', {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  })
}
